import { PublicClientApplication, LogLevel as MSALLogLevel, ILoggerCallback } from '@azure/msal-browser'
import { logger, LinkingJourneyOrigins } from '@vrw/data/src/utils'
import { OidcClient, UserManager, UserManagerSettings, WebStorageStateStore } from 'oidc-client-ts'
import {
  VAA_AUTH_CLIENT_ID,
  VAA_AUTH_ISSUER,
  PING_AUTH_URL,
  PING_ACCOUNT_SPACE,
  PING_ACR_VALUE,
  UDAL_ACR_VALUE,
  DOTCOM_URL,
  PING_WEB_CLIENT_ID,
} from '../config'
import { PATHS } from '../router/paths'
import { RedDataConfig } from '@vrw/data/src/types'
import { manuallyNavigate } from 'web/src/dataImplementation/navigation'
import { trackDatadogAction } from '@/dataImplementation/datadog'
import { VaaAuthService } from './vaaAuth'
import { getHybridAuthConfig } from '@/feature/HybridApp/helpers/authHelper'

const redBasePath = `${window.location.protocol}//${window.location.host}/virgin-red`

export const fetchConfig = (): UserManagerSettings => {
  return getHybridAuthConfig({
    automaticSilentRenew: false,
    authority: PING_AUTH_URL,
    redirect_uri: `${redBasePath}/login-success`,
    response_type: 'code',
    scope: 'openid profile email execute_api:user red_api offline_access',
    metadata: {
      authorization_endpoint: `${PING_AUTH_URL}/authorize`,
      token_endpoint: `${PING_AUTH_URL}/token`,
      end_session_endpoint: `${PING_AUTH_URL}/signoff`,
      revocation_endpoint: `${PING_AUTH_URL}/revoke`,
    },
    // library defaults to session storage which can not be shared across tabs/multiple browser sessions
    // we alway requires it to be local (Note: we will likely need to clean up our existing storage of auth)
    userStore: new WebStorageStateStore({ store: window.localStorage }),
    client_id: PING_WEB_CLIENT_ID,
    extraQueryParams: {
      audience: PING_ACCOUNT_SPACE ?? '',
      post_logout_redirect_uri: `${redBasePath}/logout-success`,
    },
  })
}

const config = fetchConfig()
// use this one for generic methods eg revoke single tokens
const genericClient = new OidcClient(config)
// use this for pretty much everything else
export const auth = new UserManager(config)

const authenticate: RedDataConfig['auth']['authenticate'] = async (options) => {
  try {
    if (options?.connection) {
      // external VAA auth
      await auth.signinRedirect({ extraQueryParams: { ...auth.settings.extraQueryParams, ...options } })
    } else {
      await auth.signinRedirect(options)
    }
  } catch (error) {
    logger.warn('oidcClient:signinRedirect() failed: ', error)
  }
}

const postLoginCallback = async () => {
  try {
    return await auth.signinCallback()
  } catch (error) {
    // redirect home if login fails
    manuallyNavigate(PATHS.HOME)

    return logger.warn('oidcClient:postLoginCallback() failed: ', error)
  }
}

const refresh = async () => {
  try {
    const response = await auth.signinSilent()

    return {
      accessToken: response?.access_token,
      idToken: response?.id_token,
      expiresIn: response?.expires_in,
    }
  } catch (error) {
    return logger.warn('oidcClient:refresh() failed: ', error)
  }
}

const revoke = async (token: string) => {
  if (!token) {
    logger.warn('oidcClient:revoke() skipped', 'Token is blank.')
    return
  }

  try {
    await genericClient.revokeToken(token)
  } catch (error) {
    logger.warn('oidcClient:revoke() failed ', error)
  }
}

const stepUp = async () => {
  try {
    await authenticate({ acr_values: PING_ACR_VALUE || '' })
  } catch (error) {
    logger.warn('oidcClient:stepUp() failed: ', error)
  }
}

const stepUpTokenParsing = async () => {
  try {
    const response = await auth.getUser()
    return {
      accessToken: response?.access_token,
    }
  } catch (error) {
    return logger.warn('oidcClient:stepUpTokenParsing() failed: ', error)
  }
}

const logout = async () => {
  try {
    await auth.signoutRedirect()
  } catch (err) {
    logger.warn('oidcClient:logout() failed: ', err)
  }
}

const msalInstance = new PublicClientApplication({
  auth: {
    clientId: VAA_AUTH_CLIENT_ID || '',
    authority: VAA_AUTH_ISSUER,
    knownAuthorities: ['avadeunloyaltyfcb2c.b2clogin.com', 'avateunloyaltyfcb2c.b2clogin.com', 'identity.virginatlantic.com'],
  },
  system: {
    loggerOptions: {
      loggerCallback: ((level: MSALLogLevel, message: string, containsPii: boolean): void => {
        if (containsPii) {
          return
        }

        let logLevel: keyof typeof logger
        switch (level) {
          case MSALLogLevel.Error:
            logLevel = 'error'
            break
          case MSALLogLevel.Warning:
            logLevel = 'warn'
            break
          default:
            logLevel = 'log'
        }

        const logData = {
          level: MSALLogLevel[level],
          message,
        }

        logger[logLevel]('MSAL Auth:', logData)
        trackDatadogAction('msal_auth_log', logData)
      }) as ILoggerCallback,
      piiLoggingEnabled: false,
      logLevel: MSALLogLevel.Info,
    },
  },
})

const handleVaaAuth = async (journeyName?: LinkingJourneyOrigins): Promise<void> => VaaAuthService.handleAuth(journeyName, redBasePath)

const getVaaToken = async (): Promise<{ idToken?: string }> => VaaAuthService.getToken()

const handleUDAL = async () => {
  try {
    await authenticate({
      acr_values: UDAL_ACR_VALUE ?? '',
      redirect_uri: `${DOTCOM_URL}/virgin-red/account/link-accounts`,
    })
  } catch (error) {
    logger.warn('oidcClient:handleUDAL() failed: ', error)
  }
}

export {
  msalInstance,
  authenticate,
  refresh,
  logout,
  revoke,
  stepUp,
  stepUpTokenParsing,
  handleVaaAuth,
  getVaaToken,
  postLoginCallback,
  handleUDAL,
}
